// Configuration for discipline rule categories
// `domainLabel`/`domainDescription` are used when a CUSTOM domain is active in
// the shared rules editor (Attendance, Health, etc.). They must read neutrally
// for any domain — no discipline nouns (mention/offence/behaviour). Mechanism
// names that are already neutral (Threshold/Escalation/Accountability) reuse the
// discipline label. Discipline + recognition copy is untouched (byte-identical).
export const RULE_CATEGORIES = [
  {
    key: 'mention',
    label: 'Mention Rules',
    recognitionLabel: 'Recognition Rules',
    domainLabel: 'Event Responses',
    icon: '⚠️',
    description: 'Define automatic responses to specific types of mentions or behaviors',
    recognitionDescription: 'Define automatic responses when students are recognised for positive behaviours',
    domainDescription: 'Create Queue follow-ups after matching Domain events'
  },
  {
    key: 'threshold',
    label: 'Threshold Rules',
    recognitionLabel: 'Milestone Rules',
    domainLabel: 'Action Thresholds',
    icon: '📊',
    description: 'Automatically trigger actions when mention counts reach certain thresholds',
    recognitionDescription: 'Automatically trigger rewards when recognition counts reach milestones',
    domainDescription: 'Create a follow-up after an existing action reaches a threshold'
  },
  {
    key: 'escalation',
    label: 'Escalation Rules',
    recognitionLabel: 'Upgrade Rules',
    domainLabel: 'Escalation Rules',
    icon: '⬆️',
    description: 'Upgrade consequence types when students accumulate too many of a specific consequence',
    recognitionDescription: 'Upgrade reward levels when students accumulate multiple recognitions of the same type',
    domainDescription: 'Replace the next matching action after a configured number has occurred'
  },
  {
    key: 'zero-tolerance',
    label: 'Zero Tolerance Rules',
    recognitionLabel: 'Spotlight Rules',
    domainLabel: 'Instant Rules',
    icon: '🚫',
    description: 'Immediate severe consequences for specific offenses with no warnings',
    recognitionDescription: 'Instant spotlight recognition for exceptional positive behaviours',
    domainDescription: 'Immediate actions for specific event types, no warnings'
  },
  {
    key: 'accountability',
    label: 'Accountability Rules',
    recognitionLabel: 'Momentum Rules',
    domainLabel: 'Accountability Rules',
    icon: '📈',
    description: 'Temporary periods of immediate consequences for repeated offenses',
    recognitionDescription: 'Track recognition streaks and reward sustained positive behaviour',
    domainDescription: 'Temporary periods of immediate action for repeated events'
  },
  {
    key: 'contract',
    label: 'Behavior Contracts',
    recognitionLabel: 'Growth Goals',
    domainLabel: 'Contracts',
    icon: '📋',
    description: 'Individual agreements with students to avoid specific behaviors over time',
    recognitionDescription: 'Individual goals for students to demonstrate positive behaviours over time',
    domainDescription: 'Individual agreements with students over time'
  }
];

// Factory function to create discipline rules manager
export const createDisciplineRulesManager = ({ saveRule, deleteRule, duplicateRule }) => {
  
  // Process rule saving - just save what the form sends
  const processRuleSave = async (formData, categoryType, existingId = null) => {
    try {
      // Validate required fields
      const ruleName = formData.title || formData.name;
      if (!ruleName || !ruleName.trim()) {
        console.error('Rule name validation failed:', { title: formData.title, name: formData.name });
        throw new Error('Rule name is required');
      }

      // Just save the form data as-is with type added
      const ruleData = {
        ...formData,
        type: categoryType
      };

      // Preserve ID for existing rules
      if (formData.id) {
        ruleData.id = formData.id;
      }

      // Save the rule
      const savedRule = await saveRule(ruleData, existingId);
      return savedRule;
    } catch (error) {
      console.error('Error processing rule save:', error);
      throw error;
    }
  };

  // Process rule deletion with confirmation
  const processRuleDelete = async (ruleId) => {
    try {
      // Additional validation could be added here
      // For example, checking if rule is currently being used
      
      await deleteRule(ruleId);
      return { success: true };
    } catch (error) {
      console.error('Error processing rule delete:', error);
      throw error;
    }
  };

  // Process rule duplication
  const processRuleDuplicate = async (ruleData) => {
    try {
      const duplicatedRule = await duplicateRule(ruleData);
      return duplicatedRule;
    } catch (error) {
      console.error('Error processing rule duplicate:', error);
      throw error;
    }
  };

  return {
    processRuleSave,
    processRuleDelete,
    processRuleDuplicate
  };
};

// Utility functions for discipline rules
export const disciplineRulesUtils = {
  // Validate rule configuration
  validateRule: (ruleData) => {
    const errors = [];

    // Simple validation for the flat structure
    const ruleName = ruleData.title || ruleData.name;
    if (!ruleName || !ruleName.trim()) {
      errors.push('Rule name is required');
    }

    if (!ruleData.type) {
      errors.push('Rule type is required');
    }

    // Type-specific validation
    switch (ruleData.type) {
      case 'threshold':
        if (!ruleData.count || ruleData.count < 1) {
          errors.push('Threshold count must be at least 1');
        }
        break;

      case 'escalation':
        if (!ruleData.threshold || ruleData.threshold < 1) {
          errors.push('Escalation threshold must be at least 1');
        }
        if (!ruleData.monitorsType) {
          errors.push('Must specify which consequence type to monitor');
        }
        if (!ruleData.escalatesTo) {
          errors.push('Must specify which consequence type to escalate to');
        }
        break;

      case 'mention':
        if (!ruleData.conditions?.offenceTypes || ruleData.conditions.offenceTypes.length === 0) {
          errors.push('At least one offence type must be selected');
        }
        break;

      case 'zero-tolerance':
        if (!ruleData.offenseTypes || ruleData.offenseTypes.length === 0) {
          errors.push('At least one offense must be selected for zero tolerance rules');
        }
        break;

      case 'contract':
        if (!ruleData.targets || ruleData.targets.length === 0) {
          errors.push('At least one target (student or grade group) must be selected');
        }
        if (!ruleData.offenseTypes || ruleData.offenseTypes.length === 0) {
          errors.push('At least one offense type must be selected');
        }
        if (!ruleData.duration || ruleData.duration < 1) {
          errors.push('Duration must be at least 1');
        }
        break;
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Get default rule data for a category
  getDefaultRuleData: (categoryType) => {
    switch (categoryType) {
      case 'threshold':
        // Simple flat structure that ThresholdRuleForm expects
        return {
          type: 'threshold',
          title: 'New Threshold Rule',
          count: 3,
          countType: 'mentions',
          timeCount: 1,
          timeframe: 'week',
          consequenceCount: 1,
          consequenceType: 'demerit',
          groups: [],
          historyFilter: null,
          active: true
        };

      case 'score-aggregation':
        // The Academics (marks) domain. Aggregates over numeric scores rather
        // than counting events — fields are consumed verbatim by
        // ScoreAggregationRuleService. `shape` = `${metric}-${direction}`.
        return {
          type: 'score-aggregation',
          title: 'New Score Rule',
          shape: 'avg-below',
          threshold: 50,
          count: 3,
          subjectScope: 'same-subject',
          timeframe: 'academic-term',
          timeCount: 5,
          childDomain: 'mentions',
          consequenceType: null,
          active: true
        };

      case 'mention':
        return {
          type: 'mention',
          title: 'New Mention Rule',
          groups: [],
          historyFilter: null,
          count: 3,
          mentionScope: 'same-offense',
          timeCount: 1,
          timeframe: 'day',
          consequenceCount: 1,
          consequenceType: 'demerit',
          resetCondition: 'after-consequence',
          active: true
        };

      case 'zero-tolerance':
        return {
          type: 'zero-tolerance',
          title: 'New Zero Tolerance Rule',
          consequenceType: 'detention',
          offenseTypes: [],
          notifyParents: true,
          notifyPolice: false,
          notifyAdmin: false,
          groups: [],
          active: true
        };

      case 'accountability':
        return {
          type: 'accountability',
          title: 'New Accountability Rule',
          triggerOffenses: [],
          triggerCount: 3,
          triggerTimeframe: 'month',
          periodLength: 7,
          extensionLength: 7,
          maxTotalPeriod: 21,
          duringConsequence: 'detention',
          quickRetrigger: true,
          quickRetriggerPeriod: 7,
          active: true
        };

      case 'contract': {
        const today = new Date().toISOString().split('T')[0];
        const calculateEndDate = (startDate, duration, durationType) => {
          const start = new Date(startDate);
          const end = new Date(start);
          switch (durationType) {
            case 'days':
              end.setDate(start.getDate() + duration);
              break;
            case 'weeks':
              end.setDate(start.getDate() + (duration * 7));
              break;
            case 'months':
              end.setMonth(start.getMonth() + duration);
              break;
            default:
              end.setDate(start.getDate() + duration);
          }
          return end.toISOString().split('T')[0];
        };

        return {
          type: 'contract',
          title: 'Behavior Contract',
          targets: [],
          offenseTypes: [],
          duration: 30,
          durationType: 'days',
          violationConsequence: 'suspension',
          startDate: today,
          endDate: calculateEndDate(today, 30, 'days'),
          active: true
        };
      }

      default:
        return {
          type: categoryType,
          name: 'New Rule',
          active: true
        };
    }
  },

  // Format rule for display
  formatRuleDisplay: (rule) => {
    let summary = '';
    
    switch (rule.type) {
      case 'threshold': {
        const count = rule.conditions?.mentionCount || 'N/A';
        const timeframe = rule.conditions?.timeframe || 'week';
        summary = `Triggers when ${count} mentions within 1 ${timeframe}`;
        break;
      }

      case 'mention': {
        const types = rule.conditions?.offenceTypes || [];
        const typeText = types.length > 0 ? types.join(', ') : 'any offence';
        summary = `Monitors ${typeText}`;
        break;
      }

      case 'zero-tolerance': {
        const offenseCount = rule.offenseTypes?.length || 0;
        const consequence = rule.consequenceType || 'detention';
        summary = `Immediate ${consequence} for ${offenseCount} selected offense${offenseCount === 1 ? '' : 's'}`;
        break;
      }

      case 'contract': {
        const targetCount = rule.targets?.length || 0;
        const offenseCount = rule.offenseTypes?.length || 0;
        const duration = rule.duration || 30;
        const durationType = rule.durationType || 'days';
        summary = `${targetCount} target${targetCount === 1 ? '' : 's'} must avoid ${offenseCount} offense${offenseCount === 1 ? '' : 's'} for ${duration} ${durationType}`;
        break;
      }

      default:
        summary = rule.description || 'Custom rule';
    }
    
    return summary;
  }
};
